import React, { useState } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BillingQuantitySourceSelectionModal, {
  BILLING_QUANTITY_FLOW_LABELS,
  BILLING_QUANTITY_FLOW_OPTIONS,
  BILLING_QUANTITY_SOURCE_LABELS,
} from "./BillingQuantitySourceSelectionModal";
import StorageCalculationMethodSelectionModal, {
  STORAGE_CALCULATION_METHOD_LABELS,
} from "./StorageCalculationMethodSelectionModal";
import { getBillingTypesArray } from "../../../utils/billingHelpers";
import { isHundredweightOnly } from "./utils/billingTypes";

const TAXABLE_CHARGE_OPTIONS = [
  { value: "inbound_charges", label: "Inbound Charges" },
  { value: "outbound_charges", label: "Outbound Charges" },
  { value: "storage_charges", label: "Storage Charges" },
  { value: "internal_work_order_charges", label: "Internal Work Order Charges" },
  { value: "material_charges", label: "Material Charges" },
  { value: "labor_charges", label: "Labor Charges" },
  { value: "equipment_charges", label: "Equipment Charges" },
  { value: "open_inspect_charges", label: "Open & Inspect Charges" },
  { value: "one_time_charges", label: "One-Time Flat Charges" },
];

export { TAXABLE_CHARGE_OPTIONS };

export default function TaxSettings({ entityLabel = "project" }) {
  const { values, setFieldValue } = useFormikContext();
  const [storageMethodModalOpen, setStorageMethodModalOpen] = useState(false);
  const [billingQuantityModalFlow, setBillingQuantityModalFlow] = useState(null);
  const selected = values.taxableChargeTypes || [];
  const isTaxExempt = selected.length === 0;
  const storageMethod = values.storageCalculationMethod || "highmark";
  const storageMethodLabel = STORAGE_CALCULATION_METHOD_LABELS[storageMethod] || STORAGE_CALCULATION_METHOD_LABELS.highmark;
  const monthlyValuationFee = values.monthlyValuationFee ?? "";
  const billingQuantityCalculationMode = values.billingQuantityCalculationMode || {};
  const colocationBillingEnabled = values.colocationBillingEnabled ?? true;
  const useMinimumChargeForStorage = values.useMinimumChargeForStorage ?? false;
  // Billing::Storage::BillableTotal applies the minimum only when Hundredweight is
  // the project's sole storage type. The client form has no billing types, so
  // there the setting stays editable.
  const projectBillingTypes = values.entities?.project?.billingTypes;
  const storageBillingTypes = getBillingTypesArray(projectBillingTypes?.storage);
  const storageMinimumSupported =
    projectBillingTypes === undefined || isHundredweightOnly(storageBillingTypes);
  const billingRules = values.billingRules || {};
  const storageRules = billingRules.storage || {};
  const gracePeriod = storageRules.grace_period || {};
  const gracePeriodEnabled = gracePeriod.enabled === true;
  const gracePeriodDays = gracePeriod.days ?? "";

  const handleStorageMethodChange = value => {
    setFieldValue("storageCalculationMethod", value);
  };

  const handleTaxExemptToggle = () => {
    if (isTaxExempt) {
      setFieldValue("taxableChargeTypes", ["labor_charges", "material_charges", "storage_charges"]);
    } else {
      setFieldValue("taxableChargeTypes", []);
    }
  };

  const handleToggle = (chargeType) => {
    const next = selected.includes(chargeType)
      ? selected.filter(t => t !== chargeType)
      : [...selected, chargeType];

    setFieldValue("taxableChargeTypes", next);
  };

  const allChecked = selected.length === TAXABLE_CHARGE_OPTIONS.length;

  const handleToggleAll = () => {
    if (allChecked) {
      setFieldValue("taxableChargeTypes", []);
    } else {
      setFieldValue("taxableChargeTypes", TAXABLE_CHARGE_OPTIONS.map(o => o.value));
    }
  };

  const handleBillingQuantityModeChange = (flow, mode) => {
    setFieldValue("billingQuantityCalculationMode", {
      ...billingQuantityCalculationMode,
      [flow]: mode,
    });
  };

  const updateGracePeriod = updates => {
    setFieldValue("billingRules", {
      ...billingRules,
      storage: {
        ...storageRules,
        grace_period: {
          behavior: "threshold_exemption",
          ...gracePeriod,
          ...updates,
        },
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Billing Quantity Source
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Control how Glimpse determines billable quantities for inbound, outbound, and storage
          charges.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use these settings when you want quantities to come from operational records instead of
          being typed in manually. Default: manual.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.keys(BILLING_QUANTITY_FLOW_LABELS).map((flow) => {
            const selectedMode = billingQuantityCalculationMode[flow] || "manual";

            return (
              <Paper key={flow} variant="outlined" sx={{ p: 2, borderColor: "grey.200" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {BILLING_QUANTITY_FLOW_LABELS[flow]}
                  </Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={() => setBillingQuantityModalFlow(flow)}
                    sx={{
                      justifyContent: "space-between",
                      minWidth: 280,
                      px: 2,
                      py: 0.75,
                      borderColor: "grey.300",
                      color: "text.primary",
                      fontWeight: 400,
                      textTransform: "none",
                      flexShrink: 0,
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "transparent",
                      },
                    }}
                  >
                    {BILLING_QUANTITY_SOURCE_LABELS[flow]?.[selectedMode]}
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Box>
        <BillingQuantitySourceSelectionModal
          open={Boolean(billingQuantityModalFlow)}
          flow={billingQuantityModalFlow}
          currentValue={
            billingQuantityModalFlow
              ? billingQuantityCalculationMode[billingQuantityModalFlow] || "manual"
              : "manual"
          }
          onClose={() => setBillingQuantityModalFlow(null)}
          onSelect={(value) => {
            if (!billingQuantityModalFlow) return;
            handleBillingQuantityModeChange(billingQuantityModalFlow, value);
          }}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Storage Calculation Method
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose how billable storage quantities are calculated for this {entityLabel}.
        </Typography>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={() => setStorageMethodModalOpen(true)}
          sx={{
            justifyContent: "space-between",
            minWidth: 280,
            px: 2,
            py: 0.75,
            borderColor: "grey.300",
            color: "text.primary",
            fontWeight: 400,
            textTransform: "none",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "transparent",
            },
          }}
        >
          {storageMethodLabel}
        </Button>
        <StorageCalculationMethodSelectionModal
          open={storageMethodModalOpen}
          currentValue={storageMethod}
          onClose={() => setStorageMethodModalOpen(false)}
          onSelect={handleStorageMethodChange}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Co-location Billing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When enabled, multiple assets sharing the same physical rack location are billed as a
          single storage charge. Disable to bill every asset for its own location, even when stacked together.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={colocationBillingEnabled}
              onChange={e => setFieldValue("colocationBillingEnabled", e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              Enable co-location billing
            </Typography>
          }
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Storage Grace Period
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assets that fully exit the warehouse within the configured window do not incur storage charges.
          Assets that remain longer are billed normally.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={gracePeriodEnabled}
              onChange={e => updateGracePeriod({ enabled: e.target.checked })}
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              Enable storage grace period
            </Typography>
          }
        />
        <Collapse in={gracePeriodEnabled}>
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
            <TextField
              size="small"
              type="number"
              label="Grace period"
              inputProps={{ min: 1, step: 1 }}
              value={gracePeriodDays}
              onChange={e => updateGracePeriod({
                days: e.target.value === "" ? "" : parseInt(e.target.value, 10),
                enabled: true,
              })}
              InputProps={{
                endAdornment: <InputAdornment position="end">days</InputAdornment>,
              }}
              sx={{ width: 220 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              If the warehouse stay equals the grace period, storage is still exempt.
            </Typography>
          </Box>
        </Collapse>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Storage Minimum Charge
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Apply the configured minimum charge once to the total Hundredweight storage charge
          for this {entityLabel} when the calculated total is lower than that minimum.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={useMinimumChargeForStorage}
              disabled={!storageMinimumSupported}
              onChange={e => setFieldValue("useMinimumChargeForStorage", e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              Apply the storage minimum charge to the {entityLabel} total
            </Typography>
          }
        />
        {!storageMinimumSupported && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {storageBillingTypes.length > 0
              ? `This setting only applies to Hundredweight storage. This ${entityLabel}'s storage is billed by ${storageBillingTypes.join(", ")}, so no storage minimum is applied.`
              : `This setting only applies to Hundredweight storage. This ${entityLabel} has no storage billing type, so no storage minimum is applied.`}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Monthly Valuation Fee
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A fixed dollar amount added as a valuation line item to every billing period for this {entityLabel}.
        </Typography>
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
          value={monthlyValuationFee}
          onChange={e => setFieldValue("monthlyValuationFee", e.target.value === "" ? "" : parseFloat(e.target.value))}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{ width: 200 }}
          placeholder="0.00"
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
          Sales Tax
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure how sales tax is applied to this {entityLabel}'s charges.
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={isTaxExempt}
                onChange={handleTaxExemptToggle}
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Tax Exempt
              </Typography>
            }
          />
          <Collapse in={!isTaxExempt}>
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Select which charge categories should have sales tax applied.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allChecked}
                    indeterminate={!allChecked && !isTaxExempt}
                    onChange={handleToggleAll}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    Select All
                  </Typography>
                }
                sx={{ mb: 1, borderBottom: "1px solid #f0f0f0", pb: 1 }}
              />
              {TAXABLE_CHARGE_OPTIONS.map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={selected.includes(value)}
                      onChange={() => handleToggle(value)}
                    />
                  }
                  label={<Typography variant="body2">{label}</Typography>}
                  sx={{ ml: 1, display: "flex" }}
                />
              ))}
            </Box>
          </Collapse>
        </FormGroup>
      </Paper>
    </Box>
  );
}
